import { GetServerSideProps } from "next";

import { fetchCMSData, fetchPageBySlug, getBlockTypes } from "@clients/contentfulClient";
import Meta from "@components/Meta/meta";
import ExtendedLandingPage from "@containers/ExtendedLandingpage";
import InsuranceHandbookPage from "@containers/InsuranceHandbookPage";
import { getFactsBreadCrumbsData } from "@containers/InsuranceHandbookPage/utils/insuranceHandbookpageHelper";
import Layout from "@containers/Layout/layout";
import { Event, trackEvent } from "@utils/events";
import { socialMediaDefaultImage } from "@utils/imageHelpers/imageHelper";
import { pathHasContent } from "@utils/pageHelpers/pathHasContent";
import { isNullOrEmpty, subString } from "@utils/string";
import { getBaseUrl, replaceImageVaultBasePath } from "@utils/urlResolver";

import { GET_INSURANCEHANDBOOKPAGE_BY_SLUG } from "queries/global/getInsuranceHandbookPageBySlug.query";
import { GET_INSURANCEHANDBOOKPAGE_BY_ID_M } from "queries/pages/getInsuranceHandbook.query";
import { getFactsStructure } from "services/FactsStructureService";
import { GetSiteTreeStructure } from "services/SiteTreeStructureFactory";
import { UrlStructureService } from "services/UrlStructureService";
import { getLayoutData } from "services/layoutService";

const Slug = ({
  data,
  startPage,
  currentUrl,
  inheritedContentArea,
  breadcrumbs,
  factsStructure,
  layoutData,
}) => {
  const description = data?.preamble ? subString(data?.preamble, 150) : "";
  const insuranceHandbookPageTitle = data?.name;
  const base = getBaseUrl();
  const metaImage =
    base +
    (replaceImageVaultBasePath(
      startPage?.seo?.ivSocialMediaImage?.[0]?.socialMediaImageVersion,
      false
    ) || socialMediaDefaultImage?.url);
  return startPage ? (
    <Layout {...layoutData}>
      <Meta
        title={startPage?.seo?.title}
        description={startPage?.seo?.description}
        keywords={startPage?.seo?.keywords?.map((i: string) => i)}
        image={metaImage}
        imageAlt={
          startPage?.seo?.ivSocialMediaImage?.[0]?.Metadata?.[0]?.Value ||
          socialMediaDefaultImage?.imageAltText
        }
        canonicalUrl={startPage?.seo?.canonicalUrl}
        url={currentUrl}
        noIndex={startPage?.seo?.noIndex}
        noFollow={startPage?.seo?.noFollow}
        breadcrumbs={layoutData?.siteHeaderData?.breadCrumbLinks}
      />
      <ExtendedLandingPage
        seo={startPage?.seo}
        pageData={startPage?.data}
        factStructure={factsStructure}
      />
    </Layout>
  ) : (
    <Layout {...layoutData}>
      <Meta
        title={insuranceHandbookPageTitle}
        description={description}
        url={base + currentUrl}
        image={base + socialMediaDefaultImage?.url}
        imageAlt={socialMediaDefaultImage?.imageAltText}
        noIndex={true}
        noFollow={true}
        breadcrumbs={layoutData?.siteHeaderData?.breadCrumbLinks}
      />
      <InsuranceHandbookPage
        pageData={data}
        inheritedContentArea={inheritedContentArea}
        breadcrumbs={breadcrumbs}
        leftmenuItems={factsStructure.structureTree}
      />
    </Layout>
  );
};
export default Slug;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getServerSideProps: GetServerSideProps = async (context) => {
  const preview = process.env.FORCE_PREVIEW === "true" || context.preview || false;
  const factsStructure = await getFactsStructure(preview);

  const slugs: string[] =
    typeof context.params.slug === "string" ? [context.params.slug] : context.params.slug ?? [];

  let slug = slugs[slugs.length - 1] || "forsakringshandboken";
  let currentPath = slugs.length > 0 ? "/forsakringshandboken/" + slugs.join("/") : "/";
  let currentUrl = "/forsakringshandboken/" + (slugs.length > 0 ? slugs.join("/") : "");
  let paginationPage = 1;

  const [urlStructure, siteTreeStructure] = await Promise.all([
    UrlStructureService(preview),
    GetSiteTreeStructure(preview),
  ]);

  const layoutData = await getLayoutData(
    urlStructure,
    siteTreeStructure,
    currentUrl,
    preview,
    false
  );

  trackEvent(Event.GET_SERVER_SIDE_PROPS, {
    path: currentUrl,
    preview,
  });

  if (slug.startsWith("sida-")) {
    paginationPage = parseInt(slug.split("-")[1], 10);
    slug = slugs[slugs.length - 2];
    currentUrl = currentUrl.substring(0, currentUrl.lastIndexOf("/"));
    currentPath = currentPath.substring(0, currentPath.lastIndexOf("/"));
  }

  if (!pathHasContent(urlStructure, currentPath, preview)) {
    return {
      notFound: true,
    };
  }
  if (slug === "forsakringshandboken") {
    const startPage = await fetchPageBySlug(
      urlStructure,
      "forsakringshandboken",
      paginationPage,
      preview
    );
    return {
      props: {
        layoutData,
        startPage: startPage?.slug,
        currentUrl,
        factsStructure,
        preview,
      },
    };
  } else {
    //Check if id is in our global structure
    let id = factsStructure?.flatStructure?.filter((item) => item?.slug === slug)[0]?.id;

    //If not and we are previewing,  then try to get the id directly from contentful
    if (preview && isNullOrEmpty(id)) {
      const query = GET_INSURANCEHANDBOOKPAGE_BY_SLUG(slug, preview);
      const { data: pageBySlug, errors } = await fetchCMSData(query, preview);
      if (pageBySlug && !errors) {
        id = pageBySlug?.insuranceHandbookPageCollection?.items?.[0]?.sys?.id;
      }
    }

    const { data: pageData, errors } = await fetchCMSData(
      GET_INSURANCEHANDBOOKPAGE_BY_ID_M(id, preview),
      preview
    );
    let data: any;

    if (pageData)
      data = await getBlockTypes(
        pageData[Object.keys(pageData)[0]],
        urlStructure,
        paginationPage,
        preview
      );
    const breadcrumbs = preview ? [] : getFactsBreadCrumbsData(factsStructure, slugs);

    const { data: insuranceStartpageData } = await fetchCMSData(
      GET_INSURANCEHANDBOOKPAGE_BY_ID_M("1HGERlqrDIE1p9wvzcAChe", preview),
      preview
    );

    const insuranceStartPageData = await getBlockTypes(
      insuranceStartpageData[Object.keys(insuranceStartpageData)[0]],
      urlStructure,
      paginationPage,
      preview
    );
    const inheritedContentArea = insuranceStartPageData?.mainAreaBottomCollection;

    if (errors && !pageData) {
      return {
        redirect: {
          permanent: false,
          destination: `/nagonting-gick-fel`,
        },
        props: {},
      };
    } else
      return {
        props: {
          layoutData,
          data,
          currentUrl,
          inheritedContentArea,
          breadcrumbs,
          factsStructure,
          preview,
        },
      };
  }
};
